import { Component, ElementRef, ViewChild, OnInit, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ServiceRouter } from 'src/app/services/core/http-service-router.service';
import { METHOD_TYPE } from 'src/app/constants/service.constants';
import { Environment } from '../../../../config/environment';
import { Config } from '../../../../config/config';
import { SpinnerComponent } from '../../../shared/spinner/spinner.component';
import { AnalyticsBean } from 'src/app/beans/analytics/analytics-module.bean';

/**
 * Select specialty component
 */
@Component({
	selector: 'app-ehe-select-speciality',
	templateUrl: './select-speciality.component.html',
	styleUrls: ['./select-speciality.component.scss'],
})
export class SelectSpecialityComponent implements OnInit, OnChanges {
	visible = true;
	selectable = true;
	removable = true;
	addOnBlur = true;
	/** Material chips separator key codes */
	separatorKeysCodes: number[] = [ENTER, COMMA];
	/** Specialities input form control */
	specialityCtrl = new FormControl();
	/** Array containing unselected specialities */
	filteredSpecialities: Observable<any[]>;
	/** Array containing selected specialities */
	specialities: any[] = [];
	/** Selected specialities bean */
	specialitiesBean: any[] = [];
	referral: any;
	subSpecialities: string[] = [];
	/** All retrieved specialities array */
	allSpecialities = [];
	/** Select specialities formgroup */
	selectSpecialityForm: FormGroup;
	userInputCustom = 0;
	/** Analytics bean */
	analyticsData: AnalyticsBean;
	/** Stepper input */
	@Input() stepper: any;
	/** Reset form input flag */
	@Input() resetForm: boolean = false;
	/** Specialities view child */
	@ViewChild('specialitiesInput', { static: true })
	specialitiesInput: ElementRef<HTMLInputElement>;
	/** Material auto complete view child */
	@ViewChild('auto', { static: true }) matAutocomplete: MatAutocomplete;
	/** Selected specialities output emitter */
	@Output() getSpecialities: EventEmitter<any>;
	/** Material auto complete trigger view child */
	@ViewChild(MatAutocompleteTrigger, { static: true }) trigger;

	/**
	 * Select specialty constructor
	 * @param serviceRouter service router
	 * @param environment environment file that supplies env values
	 * @param config config file that holds the service end points
	 * @param spinner spinner component
	 */
	constructor(
		private serviceRouter: ServiceRouter,
		private environment: Environment,
		private config: Config,
		private spinner: SpinnerComponent
	) {
		this.getSpecialities = new EventEmitter<any>();
		this.filteredSpecialities = this.specialityCtrl.valueChanges.pipe(
			startWith(null),
			map((speciality: string | null) => (speciality ? this._filter(speciality) : this.allSpecialities.slice()))
		);
		this.analyticsData = new AnalyticsBean();
		this.analyticsData.componentName = 'request-referral';
		this.analyticsData.pageUrl = '/wellness/referral/request';
	}

	/**
	 * Component change detection
	 */
	ngOnChanges() {
		if (this.resetForm) {
			this.specialities = [];
			this.initializeSelectSpecialtyForm();
		}
	}

	/**
	 * Component initialization
	 * Select specialty form initialization
	 * Calls method to retrieve list of specialities
	 */
	ngOnInit() {
		this.initializeSelectSpecialtyForm();
		this.getReferralSpecialities();
	}

	/**
	 * Invokes open panel on Material autocomplete
	 */
	onFocus() {
		this.trigger._onChange('');
		this.trigger.openPanel();
	}

	/**
	 * Initializes select specialty form
	 */
	initializeSelectSpecialtyForm() {
		this.selectSpecialityForm = new FormGroup({
			speciality: new FormControl(''),
		});
	}

	/**
	 * Retrieves list of referral specialities
	 */
	getReferralSpecialities() {
		// this.spinner.startLoader();
		this.serviceRouter
			.makeRequest(
				this.environment.getEnvironment().EPMS_PROXY_BASE_URL,
				this.config.url.referralApi.getSpecialties,
				METHOD_TYPE.SECURED_GET,
				''
			)
			.subscribe(
				response => {
					this.spinner.stopLoader();
					if (response.specialty) {
						response.specialty.forEach(specialtyElement => {
							if (specialtyElement.sub_specialty) {
								specialtyElement.sub_specialty.forEach(subSpecialtyElement => {
									this.allSpecialities.push(subSpecialtyElement);
								});
								this.allSpecialities.push(specialtyElement);
							} else {
								this.allSpecialities.push(specialtyElement);
							}
						});
					}
				},
				error => {
					this.spinner.stopLoader();
				}
			);
	}

	/**
	 * Add speciality only when MatAutocomplete is not open
	 * To make sure this does not conflict with OptionSelected Event
	 * @param event
	 */
	add(event: MatChipInputEvent): void {
		if (!this.matAutocomplete.isOpen) {
			const input = event.input;
			const value = event.value;
			const otherSubSpeciality = [];
			// Add our speciality
			if ((value || '').trim()) {
				this.specialities.push({ id: 0, name: value.trim() });

				this.userInputCustom = this.userInputCustom + 1;
				this.allSpecialities.forEach(specialtyElement => {
					if (specialtyElement.name === 'Other') {
						if (specialtyElement.sub_specialty) {
							specialtyElement.sub_specialty.push({
								id: 0,
								name: value.trim(),
							});
						} else {
							otherSubSpeciality.push({ id: 0, name: value.trim() });
							specialtyElement.sub_specialty = otherSubSpeciality;
						}
					}
				});
			}

			// Reset the input value
			if (input) {
				input.value = '';
			}

			if (this.specialities.length > 0) {
				this.selectSpecialityForm.get('speciality').setErrors(null, { emitEvent: false });
			}

			if (this.userInputCustom > 5) {
				this.selectSpecialityForm.get('speciality').setErrors({ maxUserInputExceeded: true }, { emitEvent: false });
			}
			this.specialityCtrl.setValue(null);
		}
	}

	/**
	 * Remove an added specialty
	 * @param speciality
	 */
	remove(speciality): void {
		const index = this.specialities.indexOf(speciality);

		if (index !== -1) {
			if (this.specialities[index].id === 0) {
				this.userInputCustom = this.userInputCustom - 1;
			}

			if (index >= 0) {
				this.specialities.splice(index, 1);
			}
		}

		if (this.specialities.length === 0) {
			this.selectSpecialityForm.get('speciality').setErrors({ required: true }, { emitEvent: false });
		}

		if (this.userInputCustom <= 5) {
			this.selectSpecialityForm.get('speciality').setErrors(null, { emitEvent: false });
		}
	}

	/**
	 * Adding the selected specialities to specialities array
	 * @param event
	 */
	selected(event: MatAutocompleteSelectedEvent): void {
		this.specialities.push(event.option.value);
		this.specialitiesInput.nativeElement.value = '';
		this.specialityCtrl.setValue(null);
	}

	/**
	 * Displaying corresponding sub-specialities when a specialty is entered in the autocomplete input
	 * @param value
	 */
	private _filter(value: string): string[] {
		let filterValue;
		if (value.toLowerCase !== undefined) {
			filterValue = value.toLowerCase();
		} else {
			filterValue = '';
		}
		let filteredItems = [];
		this.allSpecialities.forEach(specialtyElement => {
			if (specialtyElement.name.toLowerCase().indexOf(filterValue) === 0) {
				if (specialtyElement.sub_specialty) {
					filteredItems = specialtyElement.sub_specialty;
				} else {
					filteredItems = this.allSpecialities.filter(
						speciality => speciality.name.toLowerCase().indexOf(filterValue) === 0
					);
				}
			}
		});
		return filteredItems;
	}

	/**
	 * On clicking next send the selected specialities list to next form (Add details form)
	 */
	shareData() {
		this.analyticsData.id = 'Go Next to add details';
		this.analyticsData.redirectedTo = '/wellness/referral/request';
		this.analyticsData.placement = 'Request referral select specialty';

		if (this.specialities.length === 0) {
			this.selectSpecialityForm.get('speciality').setErrors({ required: true }, { emitEvent: false });
		}
		if (this.selectSpecialityForm.valid) {
			this.specialitiesBean = [];
			this.specialities.forEach(selectedSpec => {
				this.allSpecialities.forEach(specialtyElement => {
					if (specialtyElement.sub_specialty) {
						specialtyElement.sub_specialty.forEach(subSpecialtyElement => {
							if (subSpecialtyElement.id === selectedSpec.id) {
								if (specialtyElement.id === 72) {
									subSpecialtyElement.id = 111;
								}
								this.specialitiesBean.push({
									id: subSpecialtyElement.id,
									name: subSpecialtyElement.name,
									medicalGroup: specialtyElement.medicalGroup,
									sub_specialty: [subSpecialtyElement],
								});
							}
						});
					} else {
						if (specialtyElement.id === selectedSpec.id) {
							if (selectedSpec.medicalGroup !== undefined) {
								this.specialitiesBean.push({
									id: specialtyElement.id,
									name: specialtyElement.name,
									medicalGroup: specialtyElement.medicalGroup,
								});
							}
						}
					}
				});
			});
			if (this.selectSpecialityForm.valid) {
				this.getSpecialities.emit({
					specialities: this.specialities,
					specialitiesBean: this.specialitiesBean,
				});
				this.stepper.next();
			}
		}
	}
}
